import { addressBookStore, currentScope } from './address-book.ts';
import { addProductById, lookupProduct, repeatLastOrder } from './reorder.ts';
import { openCart, openOrder, openSignIn, pushRoute, setStack } from './router.ts';
import { currentStrings } from '../i18n/index.ts';
import { clearCart } from './cart.ts';
import { confirmCardAfterReturn, confirmPaymentAfterReturn, takePendingCardVerification } from './payment.ts';
import { isSignedIn, sessionStore } from './session.ts';
import { notify } from './toasts.ts';
import { trackOrder } from './watcher.ts';

/** Возврат со страницы эквайера: экран заказа открывается сразу, результат спрашивается у сервера. */
async function returnFromPayment(orderId: string): Promise<void> {
  openOrder(orderId);
  const strings = currentStrings();
  // Ход оплаты показывает плашка вверху (`paymentStatusStore`); уведомления только для отказа и ожидания.
  try {
    const result = await confirmPaymentAfterReturn(orderId);
    if (result.status === 'paid') {
      clearCart();
      trackOrder(orderId, 'PAID');
    } else if (result.status === 'declined') {
      notify(strings.errors['payment_declined'] ?? strings.somethingWentWrong);
    } else {
      notify(strings.paymentStillChecking);
    }
  } catch {
    notify(strings.somethingWentWrong);
  }
}

/** Возврат со страницы эквайера после привязки карты из профиля: экран карт, результат у сервера. */
async function returnFromCardVerification(): Promise<void> {
  setStack('profile', [{ name: 'cards' }]);
  const strings = currentStrings();
  const verificationId = takePendingCardVerification();
  if (!verificationId) return;
  try {
    const result = await confirmCardAfterReturn(verificationId);
    if (result.status === 'saved') notify(strings.cardSaved);
    else if (result.status === 'declined') notify(strings.errors['payment_declined'] ?? strings.somethingWentWrong);
    else notify(strings.cardStillChecking);
  } catch {
    notify(strings.somethingWentWrong);
  }
}

/**
 * Витрина зависит от адреса: товар по ссылке ищется, когда сессия проверена и адреса
 * вошедшего загружены, иначе запрос уйдёт к складу по умолчанию.
 */
function scopeReady(): Promise<void> {
  const ready = () => {
    const session = sessionStore.get();
    if (session.restoreFailed) return true;
    return session.status !== 'unknown' && (session.profile === null || addressBookStore.get().loaded);
  };
  if (ready()) return Promise.resolve();
  return new Promise((resolve) => {
    const stops = [sessionStore.subscribe(check), addressBookStore.subscribe(check)];
    function check() {
      if (!ready()) return;
      for (const stop of stops) stop();
      resolve();
    }
  });
}

/** Параметры, которые приложение понимает как переход. */
export const LAUNCH_KEYS = ['order', 'product', 'add', 'support', 'notifications', 'reorder', 'cart', 'payment', 'card'] as const;

/**
 * Переход снаружи: ссылка запуска или тап по push. `order=<id>`, `product=<id>`, `add=<id>`,
 * `support[=<orderId>]`, `notifications`, `reorder`, `cart`. Личные разделы гостю открывают
 * лист входа. Возвращает `false`, если перехода в параметрах нет.
 */
export function runLaunch(params: URLSearchParams): boolean {
  if (!LAUNCH_KEYS.some((key) => params.has(key))) return false;

  const personal = (action: () => void) => (isSignedIn() ? action() : openSignIn());
  const order = params.get('order');
  const product = params.get('product');
  const add = params.get('add');

  if (params.has('card')) personal(() => void returnFromCardVerification());
  else if (order && params.has('payment')) personal(() => void returnFromPayment(order));
  else if (order) personal(() => openOrder(order));
  else if (product) void scopeReady().then(() => lookupProduct(product)).then((item) => item && pushRoute({ name: 'product', item }, 'home'));
  else if (add) void scopeReady().then(() => addProductById(add)).then((added) => added && currentScope().kind !== 'none' && openCart());
  else if (params.has('support')) personal(() => setStack('profile', [{ name: 'support', orderId: params.get('support') || null }]));
  else if (params.has('notifications')) personal(() => setStack('home', [{ name: 'notifications' }]));
  else if (params.has('reorder')) personal(() => void repeatLastOrder().then((added) => added && openCart()));
  else if (params.has('cart')) openCart();
  return true;
}
